import React from "react";
import { X } from "lucide-react";
import { type FormikErrors } from "formik";
import { useTranslation } from "react-i18next";

import {
  Select as RedixSelect,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/utils/cn";
import { Checkbox } from "@/components/ui/checkbox";
import { ErrorMessage } from "@/components/ErrorMessage";
import { Avatar } from "./Avatar";
import { AIShimmerOverlay } from "./ai/ai-info-extractor/ai-shimmer-overlay";

export type Option = {
  label: string;
  value: string | number;
  image?: string;
};

interface SelectProps {
  label?: string;
  /** Optional node rendered next to the label (e.g. an InfoPopover hint). */
  labelHint?: React.ReactNode;
  value: string | number | string[] | number[];
  options: Option[];
  onChange: (value: string) => void;
  placeholder?: string;
  id?: string;
  name?: string;
  required?: boolean;
  disabled?: boolean;
  error?: FormikErrors<string>;
  className?: string;
  style?: {
    container?: string;
    label?: string;
    trigger?: string;
    content?: string;
    triggerIcon?: string;
    triggerStyle?: React.CSSProperties;
    iconStyle?: React.CSSProperties;
    wrapper?: React.CSSProperties;
    popperClassName?: React.CSSProperties;
  };
  showSearch?: boolean;
  searchPlaceholder?: string;
  noOptionsMessage?: string;
  loading?: boolean;
  loadingMessage?: string;
  renderContent?: (option: Option) => React.ReactNode;
  minHeight?: number;
  isCheckNeeded?: boolean;
  multiple?: boolean;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  onLoadMore?: () => void;
  showAIShimmer?: boolean;
}

export const Select: React.FC<SelectProps> = ({
  label,
  labelHint,
  error,
  style,
  value,
  options,
  name,
  className,
  onChange,
  renderContent,
  minHeight = 48,
  loading = false,
  disabled = false,
  required = false,
  multiple = false,
  showSearch = false,
  id = "dynamic-select",
  loadingMessage,
  searchPlaceholder,
  placeholder,
  noOptionsMessage,
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
  showAIShimmer = false,
}) => {
  const { t } = useTranslation(["common"]);
  const loadingText = loadingMessage ?? t("common:states.loading");
  const searchPlaceholderText =
    searchPlaceholder ?? t("common:shared_ui.search_placeholder");
  const placeholderText = placeholder ?? t("common:select.select_an_option");
  const noOptionsText =
    noOptionsMessage ?? t("common:select.no_options_available");

  const [searchQuery, setSearchQuery] = React.useState("");
  /*
    Radix Select's typeahead listens on the whole list: without this, the first
    letter lands in the search box and every later one jumps focus to a matching
    option instead, so a search never gets past one character. Escape and the
    arrow keys still reach Radix, so closing and moving through options work.
  */
  const keepTypingInSearch = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key.length === 1) e.stopPropagation();
  };
  const [selectedValue, setSelectedValue] = React.useState<string | number>(
    typeof value === "string" || typeof value === "number" ? value : "",
  );
  const [selectedValues, setSelectedValues] = React.useState<
    (string | number)[]
  >(Array.isArray(value) ? value : []);
  const [isOpen, setIsOpen] = React.useState(false);

  React.useEffect(() => {
    if (multiple && Array.isArray(value)) setSelectedValues(value);
    else if (
      !multiple &&
      (typeof value === "string" || typeof value === "number")
    )
      setSelectedValue(value);
  }, [value, multiple]);

  const filteredOptions = React.useMemo(() => {
    if (!showSearch || !searchQuery) return options;
    return options.filter((option) =>
      option.label.toLowerCase().includes(searchQuery.toLowerCase()),
    );
  }, [options, searchQuery, showSearch]);

  const handleMultiSelect = (val: string | number) => {
    const valStr = val.toString();
    let updated: (string | number)[];
    if (selectedValues.map(String).includes(valStr))
      updated = selectedValues.filter((v) => v.toString() !== valStr);
    else updated = [...selectedValues, val];

    setSelectedValues(updated);
    onChange(updated as any);
  };

  return (
    <fieldset
      className={cn(
        "relative w-full flex flex-col gap-2 justify-center text-xs",
        style?.container,
        className,
        {
          "gap-0": !label,
        },
      )}
    >
      {label && (
        <div
          className={cn(
            "flex items-center gap-1 text-xs text-border-strong font-medium",
            style?.label,
          )}
        >
          <span className="text-xs! text-border-strong! font-medium!">
            {label.toUpperCase()}
          </span>
          {required && <span className="text-red-500 ms-1">*</span>}
          {labelHint}
        </div>
      )}

      <AIShimmerOverlay show={showAIShimmer}>
        {/* SINGLE SELECT MODE */}
        {!multiple ? (
          <div
            className="w-full flex flex-col"
            style={{ minHeight: minHeight, ...(style?.wrapper || {}) }}
          >
            <RedixSelect
              value={selectedValue?.toString()}
              onValueChange={(val) => {
                if (val === "" && loading) return;
                setSelectedValue(val);
                onChange(val);
              }}
              name={name}
              open={isOpen}
              disabled={disabled}
              onOpenChange={setIsOpen}
            >
              <SelectTrigger
                id={id}
                style={style?.triggerStyle}
                iconStyle={style?.iconStyle}
                className={cn(
                  "w-full mt-0! text-xs md:text-sm h-10 md:h-12",
                  style?.trigger,
                  error && "border-red-500 focus:ring-red-500",
                )}
                iconClassName={cn(
                  "h-3 w-3 transition-transform duration-200",
                  style?.triggerIcon,
                )}
              >
                {loading ? (
                  loadingText
                ) : (
                  <SelectValue placeholder={placeholderText} />
                )}
              </SelectTrigger>
              <SelectContent
                popperClassName={style?.popperClassName}
                className={cn(style?.content, "bg-white z-999999999!")}
              >
                {showSearch && (
                  <div className="px-2 pb-2">
                    <input
                      type="text"
                      value={searchQuery}
                      placeholder={searchPlaceholderText}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onKeyDown={keepTypingInSearch}
                      className="w-full px-2 py-1 text-sm border rounded-xs focus:outline-hidden focus:ring-2 focus:ring-info"
                    />
                  </div>
                )}
                {loading ? (
                  <div className="px-2 py-2 text-sm text-gray-500 text-center">
                    {loadingText}
                  </div>
                ) : filteredOptions.length > 0 ? (
                  filteredOptions.map((option) => (
                    <SelectItem
                      key={option.value}
                      value={option.value.toString()}
                      className="text-[11px] lg:text-sm"
                    >
                      {renderContent ? (
                        renderContent(option)
                      ) : (
                        <div className="flex items-center gap-2">
                          {option.image && (
                            <Avatar
                              imageSrc={option.image}
                              className="size-8 bg-primary/10"
                            />
                          )}
                          {option.label}
                        </div>
                      )}
                    </SelectItem>
                  ))
                ) : (
                  <div className="px-2 py-2 text-sm text-gray-500 text-center">
                    {noOptionsText}
                  </div>
                )}
              </SelectContent>
            </RedixSelect>
          </div>
        ) : (
          /* MULTIPLE SELECT MODE */
          <div
            className="w-full flex flex-col"
            style={{ minHeight: minHeight, ...(style?.wrapper || {}) }}
          >
            <RedixSelect
              open={isOpen}
              onOpenChange={() => {
                setIsOpen(!isOpen);
              }}
              disabled={disabled}
              name={name}
            >
              <SelectTrigger
                id={id}
                style={style?.triggerStyle}
                iconStyle={style?.iconStyle}
                className={cn(
                  "w-full h-full! mt-0!",
                  style?.trigger,
                  error && "border-red-500 focus:ring-red-500",
                )}
                iconClassName={cn(
                  "h-4 w-4 transition-transform duration-200",
                  style?.triggerIcon,
                  isOpen && "rotate-180",
                )}
              >
                <div className="flex flex-wrap gap-1 pointer-events-auto relative z-10">
                  {selectedValues.length > 0 ? (
                    selectedValues.map((val) => {
                      const opt = options.find(
                        (o) => String(o.value) === String(val),
                      );
                      return (
                        <span
                          key={String(val)}
                          className="bg-primary/10 text-primary px-2 py-0.5 rounded-full flex items-center gap-1 text-xs relative"
                        >
                          {opt?.label ?? String(val)}
                          <button
                            type="button"
                            className="cursor-pointer text-primary ms-1 z-20 hover:opacity-70"
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              handleMultiSelect(val);
                            }}
                          >
                            <X className="size-3" />
                          </button>
                        </span>
                      );
                    })
                  ) : (
                    <span className="text-gray-400">{placeholderText}</span>
                  )}
                </div>
              </SelectTrigger>
              <SelectContent
                popperClassName={style?.popperClassName}
                className={cn(style?.content, "bg-white")}
              >
                {showSearch && (
                  <div className="px-2 pb-2">
                    <input
                      type="text"
                      value={searchQuery}
                      placeholder={searchPlaceholderText}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onKeyDown={keepTypingInSearch}
                      className="w-full px-2 py-1 text-sm border rounded-xs focus:outline-hidden focus:ring-2 focus:ring-info"
                    />
                  </div>
                )}
                {loading ? (
                  <div className="px-2 py-2 text-sm text-gray-500 text-center">
                    {loadingText}
                  </div>
                ) : filteredOptions.length > 0 ? (
                  <>
                    {filteredOptions.map((option) => {
                      const isSelected = selectedValues
                        .map(String)
                        .includes(String(option.value));
                      return (
                        <div
                          key={option.value}
                          className={`flex items-center rounded-xs px-2 py-1 gap-1 cursor-pointer ${
                            isSelected
                              ? "bg-primary-muted"
                              : "hover:bg-gray-100"
                          }`}
                          onClick={() => handleMultiSelect(option.value)}
                        >
                          <Checkbox
                            checked={isSelected}
                            className="rounded-xs size-5"
                            onCheckedChange={() =>
                              handleMultiSelect(option.value)
                            }
                          />
                          {renderContent ? renderContent(option) : option.label}
                        </div>
                      );
                    })}

                    {/* ✅ Load More */}
                    {hasNextPage && (
                      <div className="p-1 border-t mt-1">
                        <button
                          className="text-xs text-primary font-medium w-full text-start px-2 py-1"
                          disabled={isFetchingNextPage}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            onLoadMore?.();
                          }}
                        >
                          {isFetchingNextPage ? loadingText : t("common:buttons.load_more")}
                        </button>
                      </div>
                    )}
                  </>
                ) : (
                  <p className="px-2 py-2 text-sm text-gray-500 text-center">
                    {noOptionsText}
                  </p>
                )}
              </SelectContent>
            </RedixSelect>
          </div>
        )}
      </AIShimmerOverlay>
      <div className="min-h-5">
        {error && (
          <ErrorMessage
            text={error.toString()}
            className="text-xs!"
            wrapperClassName="items-start! justify-start! p-0! h-auto!"
          />
        )}
      </div>
    </fieldset>
  );
};
